import { useState, useMemo } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'
import { useTeaCategories } from '@/entities/product/api/use-categories'

export interface SearchFiltersBarProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  filters: {
    teaType?: string
    flavor?: string
    mood?: string
    date?: string
    rating?: number
  }
  onFiltersChange: (filters: SearchFiltersBarProps['filters']) => void
}

// Фолбэк категории если API не загрузится
const FALLBACK_TEA_TYPES = [
  { id: 'green', name: 'Зелёный' },
  { id: 'oolong', name: 'Улун' },
  { id: 'shu', name: 'Шу' },
  { id: 'sheng', name: 'Шэн' },
  { id: 'white', name: 'Белый' },
  { id: 'black', name: 'Красный' },
]

/**
 * Компактная панель поиска
 */
export function SearchFiltersBar({
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
}: SearchFiltersBarProps) {
  const { hapticFeedback } = useTelegram()
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  
  // Загружаем категории чая из API
  const { data: categoriesData } = useTeaCategories()
  
  // Преобразуем категории в формат для фильтров
  // Используем name в нижнем регистре как id для более надёжного сравнения
  const teaTypes = useMemo(() => {
    if (categoriesData?.data && categoriesData.data.length > 0) {
      return categoriesData.data.map(cat => ({
        id: cat.name.toLowerCase(), // Используем название в нижнем регистре
        name: cat.name,
        slug: cat.slug,
      }))
    }
    return FALLBACK_TEA_TYPES
  }, [categoriesData])

  const activeFiltersCount = Object.values(filters).filter(Boolean).length

  return (
    <>
      <div className="py-2">
        <div className="flex items-center gap-2">
          {/* Поиск */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Поиск..."
              className="w-full h-10 pl-9 pr-8 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl text-sm text-ink dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Фильтры */}
          <button
            type="button"
            onClick={() => {
              hapticFeedback.impactOccurred('light')
              setIsFiltersOpen(true)
            }}
            className={cn(
              'relative w-10 h-10 flex items-center justify-center rounded-xl border transition-all active:scale-95',
              activeFiltersCount > 0
                ? 'bg-primary/10 border-primary text-primary dark:text-primary-light'
                : 'bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-400'
            )}
          >
            <SlidersHorizontal className="w-4 h-4" />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Bottom Sheet с фильтрами */}
      <BottomSheet
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        title="Фильтры"
        height={60}
      >
        <div className="px-4 pb-6 space-y-5">
          {/* Тип чая */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Категория чая</p>
            <div className="flex flex-wrap gap-2">
              {teaTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => {
                    hapticFeedback.selectionChanged()
                    onFiltersChange({
                      ...filters,
                      teaType: filters.teaType === type.id ? undefined : type.id,
                    })
                  }}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-95',
                    filters.teaType === type.id
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 dark:bg-dark-elevated text-gray-700 dark:text-gray-300'
                  )}
                >
                  {type.name}
                </button>
              ))}
            </div>
          </div>

          {/* Рейтинг */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Минимальный рейтинг</p>
            <div className="flex gap-2">
              {[3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => {
                    hapticFeedback.selectionChanged()
                    onFiltersChange({
                      ...filters,
                      rating: filters.rating === rating ? undefined : rating,
                    })
                  }}
                  className={cn(
                    'flex-1 py-2 rounded-lg text-sm font-medium transition-all active:scale-95',
                    filters.rating === rating
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-100 dark:bg-dark-elevated text-gray-700 dark:text-gray-300'
                  )}
                >
                  {rating}+ ★
                </button>
              ))}
            </div>
          </div>

          {/* Сброс */}
          {activeFiltersCount > 0 && (
            <button
              type="button"
              onClick={() => {
                hapticFeedback.impactOccurred('light')
                onFiltersChange({})
                setIsFiltersOpen(false)
              }}
              className="w-full py-2.5 text-sm font-medium text-primary dark:text-primary-light border border-primary dark:border-primary-light rounded-xl transition-colors hover:bg-primary/5 active:scale-[0.98]"
            >
              Сбросить фильтры
            </button>
          )}
        </div>
      </BottomSheet>
    </>
  )
}
